/**
 * The HTTP client.
 *
 * Auth is a session cookie, so there is no token to store or attach — the browser
 * does it. What we *do* have to handle is CSRF: Django wants an `X-CSRFToken`
 * header on unsafe methods, echoing the value of the `csrftoken` cookie.
 */

import type { ApiErrorBody } from './errors'

const BASE = '/api/v1'

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly fields: Record<string, string[]>

  constructor(status: number, body: ApiErrorBody) {
    super(body.detail || 'Something went wrong.')
    this.name = 'ApiError'
    this.status = status
    this.code = body.code ?? 'error'
    this.fields = body.fields ?? {}
  }

  /** True when the user is simply not signed in, which the app handles by routing to login. */
  get isUnauthenticated(): boolean {
    return this.status === 401 || this.status === 403
  }

  /** First message for a field, for inline form errors. */
  fieldError(name: string): string | undefined {
    return this.fields[name]?.[0]
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(^|;\\s*)${name}=([^;]*)`))
  return match ? decodeURIComponent(match[2]) : null
}

let csrfPrimed = false

/**
 * Fetch a CSRF cookie if we do not have one yet.
 *
 * Called before the first unsafe request rather than at startup, so a signed-out
 * visitor loading the login page does not make a request they do not need.
 */
export async function primeCsrf(force = false): Promise<void> {
  if (!force && (csrfPrimed || readCookie('csrftoken'))) return
  await fetch(`${BASE}/auth/csrf`, { credentials: 'same-origin' })
  csrfPrimed = true
}

interface RequestOptions {
  method?: string
  body?: unknown
  signal?: AbortSignal
  query?: Record<string, string | number | boolean | undefined | null>
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase()

  if (UNSAFE_METHODS.has(method)) {
    await primeCsrf()
  }

  const url = new URL(`${BASE}${path}`, window.location.origin)
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value))
    }
  }

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (options.body !== undefined && !(options.body instanceof FormData)) headers['Content-Type'] = 'application/json'
  if (UNSAFE_METHODS.has(method)) {
    const token = readCookie('csrftoken')
    if (token) headers['X-CSRFToken'] = token
  }

  let response = await fetch(url, {
    method,
    headers,
    credentials: 'same-origin',
    signal: options.signal,
    body: options.body instanceof FormData ? options.body : options.body === undefined ? undefined : JSON.stringify(options.body),
  })

  // A rotated or missing CSRF cookie shows up as a 403 on an otherwise valid
  // request. Refresh it once and retry rather than bouncing the user to login.
  if (response.status === 403 && UNSAFE_METHODS.has(method)) {
    const body = await peekJson(response)
    if (body?.code !== 'forbidden') {
      await primeCsrf(true)
      const token = readCookie('csrftoken')
      if (token) headers['X-CSRFToken'] = token
      response = await fetch(url, {
        method,
        headers,
        credentials: 'same-origin',
        signal: options.signal,
        body: options.body instanceof FormData ? options.body : options.body === undefined ? undefined : JSON.stringify(options.body),
      })
    } else {
      throw new ApiError(403, body)
    }
  }

  if (response.status === 204) return undefined as T

  if (!response.ok) {
    const body = (await peekJson(response)) ?? { detail: response.statusText, code: 'error' }
    throw new ApiError(response.status, body)
  }

  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

async function peekJson(response: Response): Promise<ApiErrorBody | null> {
  try {
    const cloned = response.clone()
    const text = await cloned.text()
    return text ? (JSON.parse(text) as ApiErrorBody) : null
  } catch {
    return null
  }
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', query, signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'DELETE', query }),
}

/**
 * A POST that survives the page going away.
 *
 * `keepalive` hands the request to the browser to finish after the document is
 * gone, which a normal fetch does not: a page being unloaded has its pending
 * requests cancelled. Used for the closing play report, which is by definition
 * sent at the moment the page is disappearing.
 *
 * Deliberately fire-and-forget. There is no time to await a response, no point
 * retrying, and no one left to tell. `sendBeacon` would also work but cannot set
 * the CSRF header, and Django needs it.
 */
export function beacon(path: string, body: unknown): void {
  const token = readCookie('csrftoken')
  if (!token) return
  try {
    void fetch(new URL(`${BASE}${path}`, window.location.origin), {
      method: 'POST',
      keepalive: true,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-CSRFToken': token },
      body: JSON.stringify(body),
    }).catch(() => {})
  } catch {
    /* keepalive is unsupported or the payload is over the limit; nothing to do */
  }
}
